/**
 * CSV Template Generator for Bulk Referral Upload
 * Generates downloadable CSV templates with headers and example data
 */

export const CSV_HEADERS = [
  // Patient Information
  'patient_first_name',
  'patient_last_name',
  'patient_dob',
  'patient_phone',
  'patient_email',
  'patient_mrn',

  // Referral Case Information
  'case_name',
  'case_description',
  'assigned_to_email',
  'overall_deadline',
  'urgency_level',

  // Task 1
  'task_1_type',
  'task_1_facility_name',
  'task_1_contact_name',
  'task_1_contact_phone',
  'task_1_contact_email',
  'task_1_notes',
  'task_1_deadline',

  // Task 2
  'task_2_type',
  'task_2_facility_name',
  'task_2_contact_name',
  'task_2_contact_phone',
  'task_2_contact_email',
  'task_2_notes',
  'task_2_deadline',

  // Task 3
  'task_3_type',
  'task_3_facility_name',
  'task_3_contact_name',
  'task_3_contact_phone',
  'task_3_contact_email',
  'task_3_notes',
  'task_3_deadline',

  // Task 4
  'task_4_type',
  'task_4_facility_name',
  'task_4_contact_name',
  'task_4_contact_phone',
  'task_4_contact_email',
  'task_4_notes',
  'task_4_deadline',

  // Task 5
  'task_5_type',
  'task_5_facility_name',
  'task_5_contact_name',
  'task_5_contact_phone',
  'task_5_contact_email',
  'task_5_notes',
  'task_5_deadline',
];

export const EXAMPLE_ROWS = [
  {
    patient_first_name: 'John',
    patient_last_name: 'Doe',
    patient_dob: '1980-05-15',
    patient_phone: '571-234-5678',
    patient_email: 'john.doe@email.com',
    patient_mrn: 'MRN12345',
    case_name: 'Pre-op Cardiac Clearance',
    case_description: 'Patient needs cardiac clearance for upcoming surgery',
    assigned_to_email: '',
    overall_deadline: '',
    urgency_level: 'medium',
    task_1_type: 'Cardiology',
    task_1_facility_name: 'Johns Hopkins Cardiology',
    task_1_contact_name: 'Dr. Sarah Smith',
    task_1_contact_phone: '410-955-5000',
    task_1_contact_email: 'cardio@jh.edu',
    task_1_notes: 'Stress test required',
    task_1_deadline: '2026-08-15',
    task_2_type: 'Pulmonary',
    task_2_facility_name: 'Maryland Pulmonology Clinic',
    task_2_contact_name: 'Dr. James Wilson',
    task_2_contact_phone: '301-555-0100',
    task_2_contact_email: 'pulm@mdclinic.com',
    task_2_notes: 'PFT needed',
    task_2_deadline: '',
  },
  {
    patient_first_name: 'Jane',
    patient_last_name: 'Smith',
    patient_dob: '1965-12-20',
    patient_phone: '571-987-6543',
    patient_email: 'jane.smith@email.com',
    patient_mrn: 'MRN67890',
    case_name: 'GI Procedure Clearance',
    case_description: 'Colonoscopy medical clearance',
    assigned_to_email: '',
    overall_deadline: '',
    urgency_level: 'high',
    task_1_type: 'GI',
    task_1_facility_name: 'Georgetown Gastro Center',
    task_1_contact_name: 'Dr. Michael Brown',
    task_1_contact_phone: '202-444-2000',
    task_1_contact_email: 'gi@georgetown.edu',
    task_1_notes: 'Recent EKG on file',
    task_1_deadline: '2026-08-10',
    task_2_type: 'Anesthesia',
    task_2_facility_name: 'Georgetown Anesthesia',
    task_2_contact_name: 'Dr. Lisa Chen',
    task_2_contact_phone: '202-444-3000',
    task_2_contact_email: 'anesthesia@georgetown.edu',
    task_2_notes: 'Review medications',
    task_2_deadline: '',
    task_3_type: 'Cardiology',
    task_3_facility_name: 'Virginia Heart Institute',
    task_3_contact_name: 'Dr. Robert Taylor',
    task_3_contact_phone: '703-555-0200',
    task_3_contact_email: 'cardio@vhi.com',
    task_3_notes: 'Echo requested',
    task_3_deadline: '2026-08-12',
  },
];

/**
 * Generate CSV content from headers and data rows
 * @param {Array} headers - Array of column headers
 * @param {Array} rows - Array of row objects
 * @returns {string} CSV formatted string
 */
export const generateCsvContent = (headers, rows) => {
  const csvRows = [];

  // Add headers
  csvRows.push(headers.join(','));

  // Add data rows
  rows.forEach(row => {
    const values = headers.map(header => {
      const value = row[header] || '';
      // Escape values containing commas or quotes
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    csvRows.push(values.join(','));
  });

  return csvRows.join('\n');
};

/**
 * Generate CSV template with example data
 * @param {boolean} includeExamples - Whether to include example rows
 * @returns {string} CSV formatted string
 */
export const generateCsvTemplate = (includeExamples = true) => {
  if (includeExamples) {
    return generateCsvContent(CSV_HEADERS, EXAMPLE_ROWS);
  }

  // Just headers, no examples
  return CSV_HEADERS.join(',') + '\n';
};

/**
 * Trigger browser download of CSV template
 * @param {boolean} includeExamples - Whether to include example rows
 * @param {string} filename - Name of the downloaded file
 */
export const downloadCsvTemplate = (includeExamples = true, filename = 'referral_bulk_upload_template.csv') => {
  const csvContent = generateCsvTemplate(includeExamples);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Parse CSV file content to array of objects
 * @param {string} csvText - Raw CSV text content
 * @returns {Array} Array of row objects
 */
export const parseCsvContent = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());

  if (lines.length === 0) {
    return [];
  }

  // Parse headers
  const headers = lines[0].split(',').map(h => h.trim());

  // Parse data rows
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    const row = {};

    headers.forEach((header, index) => {
      let value = values[index] || '';
      // Remove quotes if present
      value = value.trim().replace(/^"(.*)"$/, '$1').replace(/""/g, '"');
      row[header] = value;
    });

    rows.push(row);
  }

  return rows;
};

/**
 * Validate CSV row has required fields
 * @param {Object} row - Row object to validate
 * @returns {Object} {valid: boolean, errors: Array}
 */
export const validateCsvRow = (row) => {
  const errors = [];

  // Required fields
  if (!row.patient_first_name || !row.patient_first_name.trim()) {
    errors.push('patient_first_name is required');
  }

  if (!row.patient_last_name || !row.patient_last_name.trim()) {
    errors.push('patient_last_name is required');
  }

  if (!row.patient_dob || !row.patient_dob.trim()) {
    errors.push('patient_dob is required');
  }

  // Validate at least one task type is provided
  const hasTasks = ['task_1_type', 'task_2_type', 'task_3_type', 'task_4_type', 'task_5_type']
    .some(field => row[field] && row[field].trim());

  if (!hasTasks) {
    errors.push('At least one task type (task_1_type, task_2_type, etc.) is required');
  }

  // Validate DOB format (YYYY-MM-DD)
  if (row.patient_dob && row.patient_dob.trim()) {
    const dobRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dobRegex.test(row.patient_dob.trim())) {
      errors.push('patient_dob must be in YYYY-MM-DD format');
    }
  }

  // Validate urgency level if provided
  if (row.urgency_level && row.urgency_level.trim()) {
    const validUrgency = ['low', 'medium', 'high', 'critical'];
    if (!validUrgency.includes(row.urgency_level.trim().toLowerCase())) {
      errors.push('urgency_level must be one of: low, medium, high, critical');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Generate error report CSV from failed rows
 * @param {Array} failedRows - Array of failed row objects with error info
 * @returns {string} CSV formatted string
 */
export const generateErrorReportCsv = (failedRows) => {
  if (!failedRows || failedRows.length === 0) {
    return '';
  }

  // Get all unique headers from failed rows data
  const dataHeaders = new Set();
  failedRows.forEach(item => {
    if (item.data) {
      Object.keys(item.data).forEach(key => dataHeaders.add(key));
    }
  });

  const headers = ['row_number', 'error', ...Array.from(dataHeaders)];
  const rows = failedRows.map(item => {
    const row = {
      row_number: item.row || '',
      error: item.error || '',
      ...item.data
    };
    return row;
  });

  return generateCsvContent(headers, rows);
};

/**
 * Download error report CSV
 * @param {Array} failedRows - Array of failed row objects
 */
export const downloadErrorReport = (failedRows) => {
  const csvContent = generateErrorReportCsv(failedRows);
  if (!csvContent) return;

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().slice(0, 10);
    link.setAttribute('href', url);
    link.setAttribute('download', `referral_upload_errors_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
